// src/pages/PurchaseInvoices/NewPurchaseInvoice.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, onSnapshot, query, writeBatch, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';

const styles = {
    container: { fontFamily: 'sans-serif', maxWidth: '1000px', margin: 'auto' },
    box: { background: 'white', padding: '25px', borderRadius: '8px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)', marginBottom: '20px' },
    grid: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px', marginBottom: '20px' },
    formGroup: { marginBottom: '15px', position: 'relative' },
    label: { display: 'block', marginBottom: '5px', fontWeight: 'bold' },
    input: { width: '100%', padding: '10px', border: '1px solid #ccc', borderRadius: '4px', boxSizing: 'border-box' },
    table: { width: '100%', borderCollapse: 'collapse', marginTop: '20px' },
    th: { background: '#f4f6f9', padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' },
    td: { padding: '8px 12px', borderBottom: '1px solid #eee', verticalAlign: 'middle' },
    totals: { marginTop: '20px', float: 'right', width: '300px' },
    totalRow: { display: 'flex', justifyContent: 'space-between', padding: '8px 0', fontSize: '16px' },
    grandTotal: { fontWeight: 'bold', borderTop: '2px solid #333', paddingTop: '10px' },
    button: { padding: '12px 25px', border: 'none', background: '#5c6bc0', color: 'white', borderRadius: '5px', cursor: 'pointer', fontSize: '18px', float: 'right', marginTop: '20px' },
    suggestions: { position: 'absolute', background: 'white', border: '1px solid #ccc', borderRadius: '4px', width: '100%', maxHeight: '150px', overflowY: 'auto', zIndex: 100 },
    suggestionItem: { padding: '10px', cursor: 'pointer' },
    deleteButton: { background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer', fontSize: '20px' },
    currencyInfo: { textAlign: 'right', color: '#6c757d', marginBottom: '10px' }
};

function NewPurchaseInvoice() {
    const { userData } = useAuth();
    const navigate = useNavigate();

    const [suppliers, setSuppliers] = useState([]);
    const [products, setProducts] = useState([]);
    const [productGroups, setProductGroups] = useState([]);
    const [currencyRates, setCurrencyRates] = useState({ USD: 0 });

    const [selectedSupplier, setSelectedSupplier] = useState(null);
    const [supplierNameInput, setSupplierNameInput] = useState('');

    const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().split('T')[0]);
    const [lineItems, setLineItems] = useState([]);

    const [currentItem, setCurrentItem] = useState({ productId: '', productName: '', quantity: 1, price: 0 });

    const invoiceCurrency = selectedSupplier?.currency || 'TRY';
    const exchangeRate = invoiceCurrency === 'USD' ? currencyRates.USD : 1;

    useEffect(() => {
        if (!userData?.companyId) return;
        const companyId = userData.companyId;
        const unsubSuppliers = onSnapshot(query(collection(db, 'companies', companyId, 'suppliers')), snap => setSuppliers(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
        const unsubProducts = onSnapshot(query(collection(db, 'companies', companyId, 'products')), snap => setProducts(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
        const unsubGroups = onSnapshot(query(collection(db, 'companies', companyId, 'productGroups')), snap => setProductGroups(snap.docs.map(d => ({ id: d.id, ...d.data() }))));

        const companyRef = doc(db, 'companies', companyId);
        const unsubCompany = onSnapshot(companyRef, (docSnap) => {
            if (docSnap.exists() && docSnap.data().currencySettings) {
                setCurrencyRates(docSnap.data().currencySettings);
            }
        });

        return () => { unsubSuppliers(); unsubProducts(); unsubGroups(); unsubCompany(); };
    }, [userData]);

    const handleSupplierSelect = (supplier) => {
        setSupplierNameInput(supplier.name);
        setSelectedSupplier(supplier);
        setLineItems([]);
    };

    const handleProductSelect = (product) => {
        const group = productGroups.find(g => g.id === product.groupId);
        let price = 0;
        if (group && group.defaultPurchasePrice && group.defaultPurchasePrice.currency === invoiceCurrency) {
            price = group.defaultPurchasePrice.amount || 0;
        }
        setCurrentItem({ productId: product.id, productName: product.name, quantity: 1, price });
    };

    const handleAddItem = () => {
        if (!currentItem.productId || currentItem.quantity <= 0) {
            alert("Lütfen geçerli bir ürün seçin ve miktar girin.");
            return;
        }
        setLineItems(prev => [...prev, { ...currentItem, total: currentItem.quantity * currentItem.price }]);
        setCurrentItem({ productId: '', productName: '', quantity: 1, price: 0 });
    };

    const handleItemChange = (index, field, value) => {
        const newItems = [...lineItems];
        const item = newItems[index];
        item[field] = Number(value);
        item.total = item.quantity * item.price;
        setLineItems(newItems);
    };

    const handleRemoveItem = (index) => {
        const newItems = [...lineItems];
        newItems.splice(index, 1);
        setLineItems(newItems);
    };

    const total = lineItems.reduce((acc, item) => acc + item.total, 0);

    const handleSave = async () => {
        if (!selectedSupplier || lineItems.length === 0) {
            alert("Lütfen bir tedarikçi seçin ve en az bir ürün ekleyin.");
            return;
        }

        const batch = writeBatch(db);
        const companyId = userData.companyId;

        const invoiceRef = doc(collection(db, 'companies', companyId, 'purchaseInvoices'));
        batch.set(invoiceRef, {
            supplierId: selectedSupplier.id,
            supplierName: selectedSupplier.name,
            invoiceDate: new Date(invoiceDate),
            lineItems: lineItems.map(({ productName, ...rest }) => rest),
            total,
            currency: invoiceCurrency,
            exchangeRate,
            totalTRY: total * exchangeRate
        });

        for (const item of lineItems) {
            const productRef = doc(db, 'companies', companyId, 'products', item.productId);
            const movementRef = doc(collection(productRef, 'movements'));
            const productDoc = await getDoc(productRef);
            const productData = productDoc.data();
            const newQuantity = productData.quantity + Number(item.quantity);
            batch.update(productRef, { quantity: newQuantity });

            batch.set(movementRef, {
                date: new Date(),
                type: 'Alış Faturası',
                quantity: `+${item.quantity} ${productData.unit || 'Adet'}`
            });
        }

        const supplierRef = doc(db, 'companies', companyId, 'suppliers', selectedSupplier.id);

        // --- MULTİ-CURRENCY BALANCE LOGIC ---
        const supplierCurrency = selectedSupplier.currency || 'TRY';
        let balanceImpact = total; // Default: same currency

        if (invoiceCurrency === 'USD' && supplierCurrency === 'TRY') {
            balanceImpact = total * exchangeRate; // USD to TRY
        } else if (invoiceCurrency === 'TRY' && supplierCurrency === 'USD') {
            balanceImpact = total / exchangeRate; // TRY to USD
        }

        const newBalance = (selectedSupplier.balance || 0) - balanceImpact;
        batch.update(supplierRef, { balance: newBalance });

        try {
            await batch.commit();
            navigate('/tedarikciler');
        } catch (error) {
            console.error("Alış faturası kaydedilirken hata:", error);
        }
    };

    const filteredSuppliers = supplierNameInput ? suppliers.filter(s => s.name.toLowerCase().includes(supplierNameInput.toLowerCase())) : [];
    const filteredProducts = currentItem.productName ? products.filter(p => p.name.toLowerCase().includes(currentItem.productName.toLowerCase())) : [];
    const currencySymbol = invoiceCurrency === 'USD' ? '$' : '₺';

    return (
        <div style={styles.container}>
            <h1>Yeni Alış Faturası</h1>
            <div style={styles.box}>
                <div style={styles.grid}>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Tedarikçi</label>
                        <input
                            type="text"
                            placeholder="Tedarikçi ara..."
                            style={styles.input}
                            value={supplierNameInput}
                            onChange={e => { setSupplierNameInput(e.target.value); setSelectedSupplier(null); }}
                            required
                        />
                        {filteredSuppliers.length > 0 && supplierNameInput && !selectedSupplier && (
                            <div style={styles.suggestions}>
                                {filteredSuppliers.map(s => (
                                    <div key={s.id} style={styles.suggestionItem} onMouseDown={() => handleSupplierSelect(s)}>
                                        {s.name}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Fatura Tarihi</label>
                        <input style={styles.input} type="date" value={invoiceDate} onChange={e => setInvoiceDate(e.target.value)} />
                    </div>
                </div>
                <hr />
                <h3>Ürünler</h3>
                {invoiceCurrency === 'USD' && <p style={styles.currencyInfo}>Bu fatura USD ($) olarak oluşturuluyor. Kullanılan kur: 1 USD = {exchangeRate} ₺</p>}
                <div style={{ ...styles.grid, gridTemplateColumns: '3fr 1fr 1fr 1fr', alignItems: 'flex-end' }}>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Ürün</label>
                        <input
                            type="text"
                            placeholder="Ürün ara..."
                            style={styles.input}
                            value={currentItem.productName}
                            onChange={e => setCurrentItem(prev => ({ ...prev, productName: e.target.value, productId: '' }))}
                        />
                        {filteredProducts.length > 0 && currentItem.productName && !currentItem.productId && (
                            <div style={styles.suggestions}>
                                {filteredProducts.map(p => (
                                    <div key={p.id} style={styles.suggestionItem} onMouseDown={() => handleProductSelect(p)}>
                                        {p.name}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Miktar</label>
                        <input style={styles.input} type="number" min="1" value={currentItem.quantity} onChange={e => setCurrentItem(prev => ({ ...prev, quantity: Number(e.target.value) }))} />
                    </div>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Alış Fiyatı ({currencySymbol})</label>
                        <input style={styles.input} type="number" value={currentItem.price} onChange={e => setCurrentItem(prev => ({ ...prev, price: Number(e.target.value) }))} />
                    </div>
                    <button onClick={handleAddItem} style={{ ...styles.button, width: 'auto', height: '44px', background: '#3498db' }}>Kalem Ekle</button>
                </div>
                <table style={styles.table}>
                    <thead><tr><th style={styles.th}>Ürün Adı</th><th style={styles.th}>Miktar</th><th style={styles.th}>Birim Fiyat</th><th style={styles.th}>Toplam</th><th style={styles.th}></th></tr></thead>
                    <tbody>
                        {lineItems.map((item, index) => (
                            <tr key={index}>
                                <td style={styles.td}>{item.productName}</td>
                                <td style={styles.td}><input style={{ ...styles.input, padding: '8px' }} type="number" value={item.quantity} onChange={(e) => handleItemChange(index, 'quantity', e.target.value)} /></td>
                                <td style={styles.td}><input style={{ ...styles.input, padding: '8px' }} type="number" value={item.price} onChange={(e) => handleItemChange(index, 'price', e.target.value)} /></td>
                                <td style={styles.td}>{item.total.toFixed(2)} {currencySymbol}</td>
                                <td style={styles.td}><button style={styles.deleteButton} onClick={() => handleRemoveItem(index)}>&times;</button></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <div style={{ overflow: 'hidden' }}>
                    <div style={styles.totals}>
                        <div style={{ ...styles.totalRow, ...styles.grandTotal }}><span>GENEL TOPLAM:</span> <span>{total.toFixed(2)} {currencySymbol}</span></div>
                        {invoiceCurrency === 'USD' && <div style={styles.totalRow}><span>TL Karşılığı:</span> <span>{(total * exchangeRate).toFixed(2)} ₺</span></div>}
                    </div>
                </div>
                <button onClick={handleSave} style={styles.button}>Alış Faturasını Kaydet</button>
            </div>
        </div>
    );
}

export default NewPurchaseInvoice;
